import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { FolderOpen, Plus, Pencil, Trash2, Building2, ChevronDown, ChevronRight, Shield } from 'lucide-react';

export default function AdminPortfolios() {
  const { getHeaders, API } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pfRes, outRes] = await Promise.all([
        axios.get(`${API}/admin/portfolios`, { headers: getHeaders() }),
        axios.get(`${API}/admin/outlets`, { headers: getHeaders() })
      ]);
      setPortfolios(pfRes.data);
      setOutlets(outRes.data);
    } catch {
      toast.error('Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setName(''); setLogoUrl(''); setDialogOpen(true); };
  const openEdit = (pf) => { setEditing(pf); setName(pf.name); setLogoUrl(pf.logo_url || ''); setDialogOpen(true); };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Portfolio name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/portfolios/${editing.id}`, { name: name.trim(), logo_url: logoUrl.trim() }, { headers: getHeaders() });
        toast.success('Portfolio updated');
      } else {
        await axios.post(`${API}/admin/portfolios`, { name: name.trim(), logo_url: logoUrl.trim() }, { headers: getHeaders() });
        toast.success('Portfolio created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save portfolio');
    } finally { setSaving(false); }
  };

  const handleDelete = async (pf) => {
    if (pf.is_default) { toast.error('Cannot delete the default portfolio'); return; }
    if (!window.confirm(`Delete "${pf.name}"? Outlets will be moved to the default portfolio.`)) return;
    try {
      await axios.delete(`${API}/admin/portfolios/${pf.id}`, { headers: getHeaders() });
      toast.success('Portfolio deleted');
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete');
    }
  };

  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const getOutletsForPortfolio = (pfId) => outlets.filter(o => o.portfolio_id === pfId);

  return (
    <div data-testid="admin-portfolios-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg sm:text-xl tracking-tight font-semibold text-[#1A2421]">Portfolios</h2>
          <p className="text-sm text-[#5C6B64] mt-1">Group outlets into portfolios for organized management</p>
        </div>
        <Button onClick={openCreate} className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="add-portfolio-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Portfolio
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="space-y-3">
          {portfolios.map(pf => {
            const pfOutlets = getOutletsForPortfolio(pf.id);
            const isOpen = expanded[pf.id];
            return (
              <Card key={pf.id} className="border-[#E2E8DF] shadow-sm" data-testid={`portfolio-card-${pf.id}`}>
                <CardContent className="p-0">
                  <div className="flex items-center justify-between px-4 py-3">
                    <button onClick={() => toggle(pf.id)}
                      className="flex items-center gap-2 text-left flex-1 min-w-0" data-testid={`portfolio-toggle-${pf.id}`}>
                      {isOpen ? <ChevronDown className="w-4 h-4 text-[#5C6B64]" /> : <ChevronRight className="w-4 h-4 text-[#5C6B64]" />}
                      {pf.logo_url ? (
                        <img src={pf.logo_url} alt="" className="h-5 w-5 object-contain rounded flex-shrink-0" />
                      ) : (
                        <FolderOpen className="w-4 h-4 text-[color:var(--color-primary)] flex-shrink-0" />
                      )}
                      <span className="font-medium text-[#1A2421] truncate">{pf.name}</span>
                      {pf.is_default && (
                        <Badge className="bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] border-[color:var(--color-primary)]/20 text-[10px]" data-testid="default-badge">
                          <Shield className="w-2.5 h-2.5 mr-0.5" /> Default
                        </Badge>
                      )}
                      <Badge variant="outline" className="border-[#E2E8DF] text-[#5C6B64] text-[10px] ml-1" data-testid={`portfolio-count-${pf.id}`}>
                        {pfOutlets.length} outlet{pfOutlets.length !== 1 ? 's' : ''}
                      </Badge>
                    </button>
                    <div className="flex items-center gap-1 ml-2">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(pf)}
                        className="text-[#5C6B64] hover:text-[color:var(--color-primary)] h-7 w-7 p-0" data-testid={`rename-portfolio-${pf.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      {!pf.is_default && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(pf)}
                          className="text-[#C45A41] hover:text-[#C45A41] h-7 w-7 p-0" data-testid={`delete-portfolio-${pf.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {isOpen && (
                    <div className="border-t border-[#E2E8DF] px-4 py-2 bg-[#F9F9F8]/50">
                      {pfOutlets.length === 0 ? (
                        <p className="text-xs text-[#5C6B64] py-2">No outlets in this portfolio</p>
                      ) : (
                        <div className="space-y-1">
                          {pfOutlets.map(o => (
                            <div key={o.id} className="flex items-center gap-2 py-1.5 text-sm" data-testid={`portfolio-outlet-${o.id}`}>
                              <Building2 className="w-3.5 h-3.5 text-[#5C6B64]" />
                              <span className="text-[#1A2421]">{o.name}</span>
                              <span className="text-xs text-[#5C6B64]">{o.city ? `- ${o.city}` : ''}</span>
                              <Badge className={`ml-auto text-[10px] ${o.status === 'active' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#C45A41]/10 text-[#C45A41]'}`}>
                                {o.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
          {portfolios.length === 0 && (
            <Card className="border-[#E2E8DF]">
              <CardContent className="py-10 text-center text-[#5C6B64]">
                No portfolios found
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="portfolio-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]">{editing ? 'Rename Portfolio' : 'Create Portfolio'}</DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              {editing ? `Rename "${editing.name}" to a new name.` : 'Enter a name for the new portfolio.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-sm text-[#5C6B64]">Portfolio Name <span className="text-[#C45A41]">*</span></Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. South Region"
                className="h-11 mt-1.5 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]"
                data-testid="portfolio-name-input" autoFocus />
            </div>
            <div>
              <Label className="text-sm text-[#5C6B64]">Logo URL</Label>
              <Input value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="https://example.com/logo.png"
                className="h-11 mt-1.5 bg-[#F9F9F8] border-[#E2E8DF] focus:border-[color:var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]"
                data-testid="portfolio-logo-input" />
              {logoUrl && (
                <div className="mt-2 p-2 bg-[#F9F9F8] rounded border border-[#E2E8DF] inline-block">
                  <img src={logoUrl} alt="Preview" className="h-8 w-auto object-contain"
                    onError={e => { e.target.style.display = 'none'; }} data-testid="portfolio-logo-preview" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E2E8DF] text-[#5C6B64]"
              data-testid="portfolio-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !name.trim()}
              className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="portfolio-save-btn">
              {saving ? 'Saving...' : editing ? 'Rename' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
